import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import {
  deleteEmployerLogoApi,
  EMPLOYER_LOGO_MAX_BYTES,
  EMPLOYER_LOGO_TYPES,
  getEmployerLogoBlobApi,
  uploadEmployerLogoApi,
} from "../api/settingsApi";

type Props = {
  token: string;
  employerId: string;
  /** Muda a cada troca de logotipo — é o que dispara a nova busca. */
  logoAssetId?: string | null;
  /** Chamado depois de enviar/remover, para a lista recarregar. */
  onChanged: (logoAssetId: string | null) => void;
};

/**
 * Bloco "Logotipo" do empregador (Cadastros → Empregadores). O logotipo sai no
 * cabeçalho do contracheque, dos recibos e do espelho de ponto. Fica no
 * armazenamento de uploads, mas é lido SÓ pela rota do DP — por isso a prévia vem
 * como blob. Enviar e remover valem na hora, fora do "Salvar" do formulário.
 */
export default function EmployerLogoField({ token, employerId, logoAssetId, onChanged }: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!logoAssetId) {
      setSrc(null);
      return;
    }
    let url: string | null = null;
    let cancelled = false;
    getEmployerLogoBlobApi(token, employerId)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [token, employerId, logoAssetId]);

  const handleFile = async (file?: File) => {
    if (!file) return;
    if (!EMPLOYER_LOGO_TYPES.includes(file.type)) {
      toast.error("Envie o logotipo em PNG ou JPG.");
      return;
    }
    if (file.size > EMPLOYER_LOGO_MAX_BYTES) {
      toast.error("O logotipo pode ter no máximo 1 MB.");
      return;
    }
    setBusy(true);
    try {
      await uploadEmployerLogoApi(token, employerId, file);
      toast.success("Logotipo atualizado.");
      // Chave nova para a prévia buscar de novo (o id real chega no próximo carregamento da lista).
      onChanged(`novo-${Date.now()}`);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao enviar o logotipo.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteEmployerLogoApi(token, employerId);
      toast.success("Logotipo removido.");
      setConfirmRemove(false);
      onChanged(null);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao remover o logotipo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dpFormGroup">
      <label>Logotipo</label>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div
          style={{
            width: 140,
            height: 56,
            border: "1px dashed var(--eco-border, #ccc)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
          }}
        >
          {src ? (
            <img src={src} alt="Logotipo do empregador" style={{ maxWidth: 128, maxHeight: 44, objectFit: "contain" }} />
          ) : (
            <span className="dpFormHint">Sem logotipo</span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={EMPLOYER_LOGO_TYPES.join(",")}
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          className="dpBtn dpBtn--secondary dpBtn--small"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? "Enviando..." : logoAssetId ? "Trocar" : "Enviar"}
        </button>
        {logoAssetId ? (
          <button type="button" className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setConfirmRemove(true)} disabled={busy}>
            Remover
          </button>
        ) : null}
      </div>
      <p className="dpFormHint">
        PNG ou JPG, até 1 MB. Sai no cabeçalho do contracheque, dos recibos e do espelho de ponto. Vale na hora, sem
        precisar salvar.
      </p>

      <EcoConfirmDialog
        open={confirmRemove}
        title="Remover o logotipo?"
        message="Os próximos PDFs saem sem o logotipo. Os já impressos não mudam."
        confirmLabel="Remover"
        tone="danger"
        busy={busy}
        onConfirm={remove}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
